import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import {
  reviewConfirmationModalAtom,
  useQuerySessionById,
  type ReviewActionType,
} from '@heidi/session/data';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

/**
 * Hook that gates an action behind the review confirmation modal.
 * If review confirmation is required and not yet confirmed for this session,
 * opens the modal instead of executing the action. Once confirmed (via either
 * Copy or Push to EHR), subsequent actions in the same session proceed directly.
 */
export function useReviewConfirmationGate() {
  const sessionId = useCurrentSessionId();
  const setReviewConfirmationModal = useSetAtom(reviewConfirmationModalAtom);
  const { data: userSettings } = useQueryUser((data) => ({
    is_review_modal_required: data?.session_settings?.is_review_modal_required,
  }));

  const { data: session } = useQuerySessionById({
    sessionId,
    select: (data) => ({
      is_review_modal_confirmed: data?.is_review_modal_confirmed,
    }),
  });

  const isReviewModalRequired = userSettings?.is_review_modal_required ?? false;
  const isAlreadyConfirmed = session?.is_review_modal_confirmed ?? false;

  const doActionWithConfirmation = useCallback(
    (
      action: () => void,
      actionType: ReviewActionType = 'copy',
      anchorElement: HTMLElement | null = null
    ) => {
      if (isReviewModalRequired && !isAlreadyConfirmed) {
        const anchorRect = anchorElement?.getBoundingClientRect();
        setReviewConfirmationModal({
          isOpen: true,
          onConfirm: action,
          actionType,
          dynamicAnchor: anchorRect ? { getBoundingClientRect: () => anchorRect } : null,
        });
        return false;
      }
      action();
      return true;
    },
    [isReviewModalRequired, isAlreadyConfirmed, setReviewConfirmationModal]
  );

  return { doActionWithConfirmation: doActionWithConfirmation };
}
